import { useMemo } from "react";
import { Checkbox } from "@mui/material";
import { Clinic } from "../../models";

// Clinics without a state are grouped into a trailing row
const NO_STATE = "Other";

interface ClinicStateRowsProps {
	clinics: Clinic[];
}

// Read-only display of clinics grouped into a row per state
export const ClinicStateRows = ({ clinics }: ClinicStateRowsProps) => {
	const rows = useMemo(() => {
		const groups = new Map<string, Clinic[]>();
		(clinics || []).forEach((clinic) => {
			const state = clinic.state?.trim().toUpperCase() || NO_STATE;
			const group = groups.get(state);
			group ? group.push(clinic) : groups.set(state, [clinic]);
		});

		return Array.from(groups.entries())
			.sort(([stateA], [stateB]) => {
				if (stateA === NO_STATE) {
					return 1;
				}
				if (stateB === NO_STATE) {
					return -1;
				}
				return stateA.localeCompare(stateB);
			})
			.map(([state, stateClinics]) => ({
				state,
				clinics: [...stateClinics].sort((a, b) => a.name.localeCompare(b.name))
			}));
	}, [clinics]);

	if (rows.length === 0) {
		return <></>;
	}

	return (
		<div style={{ display: "flex", flexDirection: "column" }}>
			{rows.map(({ state, clinics: stateClinics }, index) => (
				<div
					key={state}
					style={{
						display: "flex",
						alignItems: "center",
						gap: 24,
						padding: "12px",
						borderTop: index === 0 ? "none" : "solid 1px #e0e0e0"
					}}
				>
					<div style={{ fontWeight: 600, minWidth: 60 }}>{state}</div>
					<div
						style={{
							display: "flex",
							flexWrap: "wrap",
							alignItems: "center",
							columnGap: 24,
							rowGap: 4
						}}
					>
						{stateClinics.map((clinic) => (
							<div
								key={clinic.id}
								style={{
									display: "flex",
									alignItems: "center",
									gap: 8,
									minWidth: 180
								}}
							>
								<Checkbox
									checked
									readOnly
									disableRipple
									tabIndex={-1}
									sx={{ padding: 0, cursor: "default" }}
								/>
								{clinic.name}
							</div>
						))}
					</div>
				</div>
			))}
		</div>
	);
};
